import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  useLocation,
  generatePath,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  FooterContentPosition,
  ajvResolver,
  PermissionEnum,
  Popup,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import BlacklistCreateForm from '../../components/blacklist/create';
import { onBlacklistUpdate } from './store/slices/item';
import { Blacklist, BlacklistFormValues, BlacklistSettings } from '../types';
import enableAll from './helpers/enable-all';
import {
  useUpdateBlacklistMutation,
  useUpdateBlacklistSettingsMutation,
  useToggleBlacklistStatusMutation,
} from './store/services';

import cn from './blacklist.module.css';

import ROUTES from '../../routes';

const defaultSettings = {
  corporateClients: false,
  corporateClientsAdverseMedia: false,
  corporateClientsEnforcement: false,
  corporateClientsPEP: false,
  corporateClientsSOESWF: false,
  corporateClientsSanction: false,
  individualClients: false,
  individualClientsAdverseMedia: false,
  individualClientsEnforcement: false,
  individualClientsPEP: false,
  individualClientsSOESWF: false,
  individualClientsSanction: false,
  paymentOrderPayersPayees: false,
  paymentOrderPayersPayeesAdverseMedia: false,
  paymentOrderPayersPayeesEnforcement: false,
  paymentOrderPayersPayeesPEP: false,
  paymentOrderPayersPayeesSOESWF: false,
  paymentOrderPayersPayeesSanction: false,
  transactionsCorporateClients: false,
  transactionsCorporateClientsAdverseMedia: false,
  transactionsCorporateClientsEnforcement: false,
  transactionsCorporateClientsPEP: false,
  transactionsCorporateClientsSOESWF: false,
  transactionsCorporateClientsSanction: false,
  transactionsCorporatePayersPayees: false,
  transactionsCorporatePayersPayeesAdverseMedia: false,
  transactionsCorporatePayersPayeesEnforcement: false,
  transactionsCorporatePayersPayeesPEP: false,
  transactionsCorporatePayersPayeesSOESWF: false,
  transactionsCorporatePayersPayeesSanction: false,
  transactionsCounterpartiesBanks: false,
  transactionsCounterpartiesBanksAdverseMedia: false,
  transactionsCounterpartiesBanksEnforcement: false,
  transactionsCounterpartiesBanksPEP: false,
  transactionsCounterpartiesBanksSOESWF: false,
  transactionsCounterpartiesBanksSanction: false,
  transactionsIndividualClients: false,
  transactionsIndividualClientsAdverseMedia: false,
  transactionsIndividualClientsEnforcement: false,
  transactionsIndividualClientsPEP: false,
  transactionsIndividualClientsSOESWF: false,
  transactionsIndividualClientsSanction: false,
  transactionsIndividualPayersPayees: false,
  transactionsIndividualPayersPayeesAdverseMedia: false,
  transactionsIndividualPayersPayeesEnforcement: false,
  transactionsIndividualPayersPayeesPEP: false,
  transactionsIndividualPayersPayeesSOESWF: false,
  transactionsIndividualPayersPayeesSanction: false,
};

const schema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 2,
      errorMessage: 'Name is required',
    },
  },
  required: ['name'],
};

const ItemSettings = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [allSettingsEnabled, setAllSettingsEnabled] = useState(false);
  const [settings, setSettings] = useState<BlacklistSettings>(defaultSettings);
  const [defaultSettingsValues, setDefaultSettingsValues] =
    useState<BlacklistSettings>();
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [blacklistData, setBlacklistData] = useState<Blacklist | undefined>(
    undefined,
  );
  const [defaultBlacklist, setDefaultBlacklist] = useState<any>();
  const [globalError, setGlobalError] = useState('');

  const {
    item: { blacklist },
  } = useAppSelector((state) => state.blacklist);

  const { activePermissions } = useAppSelector(
    (state) => state.amlDictionaries,
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<BlacklistFormValues>({
    resolver: ajvResolver(schema),
    defaultValues: defaultSettings,
  });

  const fields = watch();

  const isDirty = !isEqual(
    {
      name: fields?.name,
      description: fields?.description,
      corporateClients: fields?.corporateClients,
      corporateClientsAdverseMedia: fields?.corporateClientsAdverseMedia,
      corporateClientsEnforcement: fields?.corporateClientsEnforcement,
      corporateClientsPEP: fields?.corporateClientsPEP,
      corporateClientsSOESWF: fields?.corporateClientsSOESWF,
      corporateClientsSanction: fields?.corporateClientsSanction,
      individualClients: fields?.individualClients,
      individualClientsAdverseMedia: fields?.individualClientsAdverseMedia,
      individualClientsEnforcement: fields?.individualClientsEnforcement,
      individualClientsPEP: fields?.individualClientsPEP,
      individualClientsSOESWF: fields?.individualClientsSOESWF,
      individualClientsSanction: fields?.individualClientsSanction,
      paymentOrderPayersPayees: fields?.paymentOrderPayersPayees,
      paymentOrderPayersPayeesAdverseMedia:
        fields?.paymentOrderPayersPayeesAdverseMedia,
      paymentOrderPayersPayeesEnforcement:
        fields?.paymentOrderPayersPayeesEnforcement,
      paymentOrderPayersPayeesPEP: fields?.paymentOrderPayersPayeesPEP,
      paymentOrderPayersPayeesSOESWF: fields?.paymentOrderPayersPayeesSOESWF,
      paymentOrderPayersPayeesSanction:
        fields?.paymentOrderPayersPayeesSanction,
      transactionsCorporateClients: fields?.transactionsCorporateClients,
      transactionsCorporateClientsAdverseMedia:
        fields?.transactionsCorporateClientsAdverseMedia,
      transactionsCorporateClientsEnforcement:
        fields?.transactionsCorporateClientsEnforcement,
      transactionsCorporateClientsPEP: fields?.transactionsCorporateClientsPEP,
      transactionsCorporateClientsSOESWF:
        fields?.transactionsCorporateClientsSOESWF,
      transactionsCorporateClientsSanction:
        fields?.transactionsCorporateClientsSanction,
      transactionsCorporatePayersPayees:
        fields?.transactionsCorporatePayersPayees,
      transactionsCorporatePayersPayeesAdverseMedia:
        fields?.transactionsCorporatePayersPayeesAdverseMedia,
      transactionsCorporatePayersPayeesEnforcement:
        fields?.transactionsCorporatePayersPayeesEnforcement,
      transactionsCorporatePayersPayeesPEP:
        fields?.transactionsCorporatePayersPayeesPEP,
      transactionsCorporatePayersPayeesSOESWF:
        fields?.transactionsCorporatePayersPayeesSOESWF,
      transactionsCorporatePayersPayeesSanction:
        fields?.transactionsCorporatePayersPayeesSanction,
      transactionsCounterpartiesBanks: fields?.transactionsCounterpartiesBanks,
      transactionsCounterpartiesBanksAdverseMedia:
        fields?.transactionsCounterpartiesBanksAdverseMedia,
      transactionsCounterpartiesBanksEnforcement:
        fields?.transactionsCounterpartiesBanksEnforcement,
      transactionsCounterpartiesBanksPEP:
        fields?.transactionsCounterpartiesBanksPEP,
      transactionsCounterpartiesBanksSOESWF:
        fields?.transactionsCounterpartiesBanksSOESWF,
      transactionsCounterpartiesBanksSanction:
        fields?.transactionsCounterpartiesBanksSanction,
      transactionsIndividualClients: fields?.transactionsIndividualClients,
      transactionsIndividualClientsAdverseMedia:
        fields?.transactionsIndividualClientsAdverseMedia,
      transactionsIndividualClientsEnforcement:
        fields?.transactionsIndividualClientsEnforcement,
      transactionsIndividualClientsPEP:
        fields?.transactionsIndividualClientsPEP,
      transactionsIndividualClientsSOESWF:
        fields?.transactionsIndividualClientsSOESWF,
      transactionsIndividualClientsSanction:
        fields?.transactionsIndividualClientsSanction,
      transactionsIndividualPayersPayees:
        fields?.transactionsIndividualPayersPayees,
      transactionsIndividualPayersPayeesAdverseMedia:
        fields?.transactionsIndividualPayersPayeesAdverseMedia,
      transactionsIndividualPayersPayeesEnforcement:
        fields?.transactionsIndividualPayersPayeesEnforcement,
      transactionsIndividualPayersPayeesPEP:
        fields?.transactionsIndividualPayersPayeesPEP,
      transactionsIndividualPayersPayeesSOESWF:
        fields?.transactionsIndividualPayersPayeesSOESWF,
      transactionsIndividualPayersPayeesSanction:
        fields?.transactionsIndividualPayersPayeesSanction,
    },
    {
      name: defaultBlacklist?.name,
      description: defaultBlacklist?.description,
      corporateClients: defaultBlacklist?.corporateClients,
      corporateClientsAdverseMedia:
        defaultBlacklist?.corporateClientsAdverseMedia,
      corporateClientsEnforcement:
        defaultBlacklist?.corporateClientsEnforcement,
      corporateClientsPEP: defaultBlacklist?.corporateClientsPEP,
      corporateClientsSOESWF: defaultBlacklist?.corporateClientsSOESWF,
      corporateClientsSanction: defaultBlacklist?.corporateClientsSanction,
      individualClients: defaultBlacklist?.individualClients,
      individualClientsAdverseMedia:
        defaultBlacklist?.individualClientsAdverseMedia,
      individualClientsEnforcement:
        defaultBlacklist?.individualClientsEnforcement,
      individualClientsPEP: defaultBlacklist?.individualClientsPEP,
      individualClientsSOESWF: defaultBlacklist?.individualClientsSOESWF,
      individualClientsSanction: defaultBlacklist?.individualClientsSanction,
      paymentOrderPayersPayees: defaultBlacklist?.paymentOrderPayersPayees,
      paymentOrderPayersPayeesAdverseMedia:
        defaultBlacklist?.paymentOrderPayersPayeesAdverseMedia,
      paymentOrderPayersPayeesEnforcement:
        defaultBlacklist?.paymentOrderPayersPayeesEnforcement,
      paymentOrderPayersPayeesPEP:
        defaultBlacklist?.paymentOrderPayersPayeesPEP,
      paymentOrderPayersPayeesSOESWF:
        defaultBlacklist?.paymentOrderPayersPayeesSOESWF,
      paymentOrderPayersPayeesSanction:
        defaultBlacklist?.paymentOrderPayersPayeesSanction,
      transactionsCorporateClients:
        defaultBlacklist?.transactionsCorporateClients,
      transactionsCorporateClientsAdverseMedia:
        defaultBlacklist?.transactionsCorporateClientsAdverseMedia,
      transactionsCorporateClientsEnforcement:
        defaultBlacklist?.transactionsCorporateClientsEnforcement,
      transactionsCorporateClientsPEP:
        defaultBlacklist?.transactionsCorporateClientsPEP,
      transactionsCorporateClientsSOESWF:
        defaultBlacklist?.transactionsCorporateClientsSOESWF,
      transactionsCorporateClientsSanction:
        defaultBlacklist?.transactionsCorporateClientsSanction,
      transactionsCorporatePayersPayees:
        defaultBlacklist?.transactionsCorporatePayersPayees,
      transactionsCorporatePayersPayeesAdverseMedia:
        defaultBlacklist?.transactionsCorporatePayersPayeesAdverseMedia,
      transactionsCorporatePayersPayeesEnforcement:
        defaultBlacklist?.transactionsCorporatePayersPayeesEnforcement,
      transactionsCorporatePayersPayeesPEP:
        defaultBlacklist?.transactionsCorporatePayersPayeesPEP,
      transactionsCorporatePayersPayeesSOESWF:
        defaultBlacklist?.transactionsCorporatePayersPayeesSOESWF,
      transactionsCorporatePayersPayeesSanction:
        defaultBlacklist?.transactionsCorporatePayersPayeesSanction,
      transactionsCounterpartiesBanks:
        defaultBlacklist?.transactionsCounterpartiesBanks,
      transactionsCounterpartiesBanksAdverseMedia:
        defaultBlacklist?.transactionsCounterpartiesBanksAdverseMedia,
      transactionsCounterpartiesBanksEnforcement:
        defaultBlacklist?.transactionsCounterpartiesBanksEnforcement,
      transactionsCounterpartiesBanksPEP:
        defaultBlacklist?.transactionsCounterpartiesBanksPEP,
      transactionsCounterpartiesBanksSOESWF:
        defaultBlacklist?.transactionsCounterpartiesBanksSOESWF,
      transactionsCounterpartiesBanksSanction:
        defaultBlacklist?.transactionsCounterpartiesBanksSanction,
      transactionsIndividualClients:
        defaultBlacklist?.transactionsIndividualClients,
      transactionsIndividualClientsAdverseMedia:
        defaultBlacklist?.transactionsIndividualClientsAdverseMedia,
      transactionsIndividualClientsEnforcement:
        defaultBlacklist?.transactionsIndividualClientsEnforcement,
      transactionsIndividualClientsPEP:
        defaultBlacklist?.transactionsIndividualClientsPEP,
      transactionsIndividualClientsSOESWF:
        defaultBlacklist?.transactionsIndividualClientsSOESWF,
      transactionsIndividualClientsSanction:
        defaultBlacklist?.transactionsIndividualClientsSanction,
      transactionsIndividualPayersPayees:
        defaultBlacklist?.transactionsIndividualPayersPayees,
      transactionsIndividualPayersPayeesAdverseMedia:
        defaultBlacklist?.transactionsIndividualPayersPayeesAdverseMedia,
      transactionsIndividualPayersPayeesEnforcement:
        defaultBlacklist?.transactionsIndividualPayersPayeesEnforcement,
      transactionsIndividualPayersPayeesPEP:
        defaultBlacklist?.transactionsIndividualPayersPayeesPEP,
      transactionsIndividualPayersPayeesSOESWF:
        defaultBlacklist?.transactionsIndividualPayersPayeesSOESWF,
      transactionsIndividualPayersPayeesSanction:
        defaultBlacklist?.transactionsIndividualPayersPayeesSanction,
    },
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { search } = useLocation();
  const { itemId } = useParams();

  const [updateBlacklist, { isLoading: requestInProgress }] =
    useUpdateBlacklistMutation();
  const [updateBlacklistSettings, { isLoading: updateSettingsInProgress }] =
    useUpdateBlacklistSettingsMutation();
  const [toggleBlacklistStatus, { isLoading: changeStatusInProgress }] =
    useToggleBlacklistStatusMutation();

  const toggleActive = async (active: boolean) => {
    setValue('active', active);

    await toggleBlacklistStatus({
      active,
      id: itemId as string,
    });
  };

  const onFormSubmit = handleSubmit(async (values) => {
    const body: {
      name: string;
      description?: string;
    } = {
      name: values.name,
    };

    if (values.description) {
      body.description = values.description;
    }

    const response = (await updateBlacklist({
      body,
      id: itemId as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError,
      });
    }

    if (!isEqual(defaultSettingsValues, settings)) {
      setDefaultSettingsValues(settings);

      const updateResponse = (await updateBlacklistSettings({
        id: itemId,
        body: settings,
      })) as { error?: ResponseError };

      if (updateResponse?.error) {
        responseErrorsResolver({
          errors: updateResponse?.error,
          setError: () => {},
          setGlobalError,
        });
      }
    }

    setDefaultBlacklist({
      id: blacklist?.id,
      name: values.name,
      description: values?.description || '',
      active: values.active,
      ...settings,
    });

    if (blacklist) {
      dispatch(
        onBlacklistUpdate({
          id: blacklist.id,
          name: values.name,
          active: values.active,
          description: values.description,
          successfulSyncLastDate: blacklist.successfulSyncLastDate,
          lastTrySyncDate: blacklist.lastTrySyncDate,
          nextTrySyncDate: blacklist.nextTrySyncDate,
          updatedAt: blacklist.updatedAt,
          source: blacklist.source,
          settings: {
            ...settings,
            id: blacklist.settings?.id as string,
            blacklistId: blacklist.id,
          },
          data: blacklist.data,
          createdAt: blacklist.createdAt,
          default: blacklist.default,
          link: blacklist.link,
        }),
      );
    }
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setBlacklistData(undefined);

    const url = generatePath(ROUTES.BLACKLIST_ITEM, {
      itemId: itemId as string,
    });

    setTimeout(() => {
      navigate(`${url}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);
  }, [dispatch]);

  useEffect(() => {
    if (blacklist && !blacklistData) {
      if (blacklist.settings) {
        const activeSettings = Object.keys(blacklist.settings).filter(
          (key) =>
            typeof (blacklist.settings as BlacklistSettings)[
              key as keyof BlacklistSettings
            ] === 'boolean' &&
            (blacklist.settings as BlacklistSettings)[
              key as keyof BlacklistSettings
            ],
        );

        const blacklistSettings = Object.keys(blacklist.settings).filter(
          (key) =>
            typeof (blacklist.settings as BlacklistSettings)[
              key as keyof BlacklistSettings
            ] === 'boolean',
        );

        if (activeSettings.length === blacklistSettings.length) {
          setAllSettingsEnabled(true);
        }
      }

      const blacklistSettings = {
        corporateClients: blacklist?.settings?.corporateClients,
        corporateClientsAdverseMedia:
          blacklist?.settings?.corporateClientsAdverseMedia,
        corporateClientsEnforcement:
          blacklist?.settings?.corporateClientsEnforcement,
        corporateClientsPEP: blacklist?.settings?.corporateClientsPEP,
        corporateClientsSOESWF: blacklist?.settings?.corporateClientsSOESWF,
        corporateClientsSanction: blacklist?.settings?.corporateClientsSanction,
        individualClients: blacklist?.settings?.individualClients,
        individualClientsAdverseMedia:
          blacklist?.settings?.individualClientsAdverseMedia,
        individualClientsEnforcement:
          blacklist?.settings?.individualClientsEnforcement,
        individualClientsPEP: blacklist?.settings?.individualClientsPEP,
        individualClientsSOESWF: blacklist?.settings?.individualClientsSOESWF,
        individualClientsSanction:
          blacklist?.settings?.individualClientsSanction,
        paymentOrderPayersPayees: blacklist?.settings?.paymentOrderPayersPayees,
        paymentOrderPayersPayeesAdverseMedia:
          blacklist?.settings?.paymentOrderPayersPayeesAdverseMedia,
        paymentOrderPayersPayeesEnforcement:
          blacklist?.settings?.paymentOrderPayersPayeesEnforcement,
        paymentOrderPayersPayeesPEP:
          blacklist?.settings?.paymentOrderPayersPayeesPEP,
        paymentOrderPayersPayeesSOESWF:
          blacklist?.settings?.paymentOrderPayersPayeesSOESWF,
        paymentOrderPayersPayeesSanction:
          blacklist?.settings?.paymentOrderPayersPayeesSanction,
        transactionsCorporateClients:
          blacklist?.settings?.transactionsCorporateClients,
        transactionsCorporateClientsAdverseMedia:
          blacklist?.settings?.transactionsCorporateClientsAdverseMedia,
        transactionsCorporateClientsEnforcement:
          blacklist?.settings?.transactionsCorporateClientsEnforcement,
        transactionsCorporateClientsPEP:
          blacklist?.settings?.transactionsCorporateClientsPEP,
        transactionsCorporateClientsSOESWF:
          blacklist?.settings?.transactionsCorporateClientsSOESWF,
        transactionsCorporateClientsSanction:
          blacklist?.settings?.transactionsCorporateClientsSanction,
        transactionsCorporatePayersPayees:
          blacklist?.settings?.transactionsCorporatePayersPayees,
        transactionsCorporatePayersPayeesAdverseMedia:
          blacklist?.settings?.transactionsCorporatePayersPayeesAdverseMedia,
        transactionsCorporatePayersPayeesEnforcement:
          blacklist?.settings?.transactionsCorporatePayersPayeesEnforcement,
        transactionsCorporatePayersPayeesPEP:
          blacklist?.settings?.transactionsCorporatePayersPayeesPEP,
        transactionsCorporatePayersPayeesSOESWF:
          blacklist?.settings?.transactionsCorporatePayersPayeesSOESWF,
        transactionsCorporatePayersPayeesSanction:
          blacklist?.settings?.transactionsCorporatePayersPayeesSanction,
        transactionsCounterpartiesBanks:
          blacklist?.settings?.transactionsCounterpartiesBanks,
        transactionsCounterpartiesBanksAdverseMedia:
          blacklist?.settings?.transactionsCounterpartiesBanksAdverseMedia,
        transactionsCounterpartiesBanksEnforcement:
          blacklist?.settings?.transactionsCounterpartiesBanksEnforcement,
        transactionsCounterpartiesBanksPEP:
          blacklist?.settings?.transactionsCounterpartiesBanksPEP,
        transactionsCounterpartiesBanksSOESWF:
          blacklist?.settings?.transactionsCounterpartiesBanksSOESWF,
        transactionsCounterpartiesBanksSanction:
          blacklist?.settings?.transactionsCounterpartiesBanksSanction,
        transactionsIndividualClients:
          blacklist?.settings?.transactionsIndividualClients,
        transactionsIndividualClientsAdverseMedia:
          blacklist?.settings?.transactionsIndividualClientsAdverseMedia,
        transactionsIndividualClientsEnforcement:
          blacklist?.settings?.transactionsIndividualClientsEnforcement,
        transactionsIndividualClientsPEP:
          blacklist?.settings?.transactionsIndividualClientsPEP,
        transactionsIndividualClientsSOESWF:
          blacklist?.settings?.transactionsIndividualClientsSOESWF,
        transactionsIndividualClientsSanction:
          blacklist?.settings?.transactionsIndividualClientsSanction,
        transactionsIndividualPayersPayees:
          blacklist?.settings?.transactionsIndividualPayersPayees,
        transactionsIndividualPayersPayeesAdverseMedia:
          blacklist?.settings?.transactionsIndividualPayersPayeesAdverseMedia,
        transactionsIndividualPayersPayeesEnforcement:
          blacklist?.settings?.transactionsIndividualPayersPayeesEnforcement,
        transactionsIndividualPayersPayeesPEP:
          blacklist?.settings?.transactionsIndividualPayersPayeesPEP,
        transactionsIndividualPayersPayeesSOESWF:
          blacklist?.settings?.transactionsIndividualPayersPayeesSOESWF,
        transactionsIndividualPayersPayeesSanction:
          blacklist?.settings?.transactionsIndividualPayersPayeesSanction,
      };

      const body: {
        id: string;
        name: string;
        description: string;
        active: boolean;
      } = {
        id: blacklist.id,
        name: blacklist.name,
        description: blacklist?.description || '',
        active: blacklist.active,
        ...blacklistSettings,
      };

      reset(body);
      setDefaultBlacklist(body);
      setBlacklistData(blacklist);

      setSettings(blacklistSettings as BlacklistSettings);
      setDefaultSettingsValues(blacklistSettings as BlacklistSettings);
    }
  }, [reset, blacklist, blacklistData]);

  const isEditable = activePermissions?.includes(
    PermissionEnum.BLACKLISTS_EDIT_ACTION,
  );

  return (
    <>
      <SidePanel
        type={SidePanelType.Cards}
        closeOnClickOutside
        isOpen={panelIsOpen}
        errorSidepanel={globalError}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={isEditable ? 'Edit settings' : 'Settings'}
        sidepanelExpandedKey="blacklist-settings"
        footer={
          isEditable ? (
            <ButtonPrimary
              text={
                requestInProgress || updateSettingsInProgress
                  ? 'Loading...'
                  : 'Save'
              }
              className={cn.createBlacklistSubmit}
              onClick={onFormSubmit}
              disabled={!isDirty}
              bold
            />
          ) : (
            <ButtonPrimary
              text="Close"
              className={cn.createBlacklistSubmit}
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          )
        }
        footerContentPosition={FooterContentPosition.Center}
      >
        <BlacklistCreateForm
          control={control}
          errors={errors}
          defaultBlacklist={blacklist?.default}
          allSettingsEnabled={allSettingsEnabled}
          blacklistId={blacklist?.id || ''}
          setAllSettingsEnabled={setAllSettingsEnabled}
          isEditable={isEditable}
          fields={fields}
          setValue={setValue}
          enableAll={() =>
            enableAll({
              getValues,
              defaultSettings,
              allSettingsEnabled,
              setAllSettingsEnabled,
              reset,
              setSettings,
            })
          }
          settings={settings}
          setSettings={setSettings}
          toggleActive={toggleActive}
          changeStatusInProgress={changeStatusInProgress}
          settingsId={blacklist?.settings?.id || ''}
        />
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default ItemSettings;
